import React, { useState, useEffect, useMemo } from 'react';
import './AdminUsers.css';
import './StudentCourses.css';
import { apiFetch } from './api';


function StudentCourses({ studentId }) {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    if (studentId) fetchCourses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studentId]);

  useEffect(() => {
    if (!studentId) return;
    const interval = setInterval(() => {
      fetchCourses(true).catch(() => {});
    }, 30000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studentId]);

  const fetchCourses = async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      const response = await apiFetch(`/student/courses`);
      const data = await response.json();
      if (data.success) setCourses(data.courses || []);
      else if (!silent) setError(data.message || 'Dersler yüklenemedi');
    } catch {
      if (!silent) setError('Bağlantı hatası');
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const filteredCourses = useMemo(() => {
    const q = searchQuery.toLocaleLowerCase('tr');
    if (!q) return courses;
    return courses.filter((c) =>
      (c.course_code || '').toLocaleLowerCase('tr').includes(q) ||
      (c.course_name || '').toLocaleLowerCase('tr').includes(q) ||
      (c.faculty_name || '').toLocaleLowerCase('tr').includes(q) ||
      (c.program_name || '').toLocaleLowerCase('tr').includes(q)
    );
  }, [courses, searchQuery]);

  if (loading) {
    return (
      <div className="au-page">
        <div className="au-toolbar">
          <h2 className="au-title">Derslerim</h2>
        </div>
        <div className="sc-grid">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="sc-card">
              <div className="au-skeleton-block" style={{ width: 60, height: 11 }} />
              <div className="au-skeleton-block" style={{ width: '85%' }} />
              <div className="au-skeleton-block" style={{ width: '60%', height: 11 }} />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="au-page">
        <div className="au-toolbar">
          <h2 className="au-title">Derslerim</h2>
        </div>
        <div className="au-flash error">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="au-page">
      <div className="au-toolbar">
        <div className="au-toolbar-top">
          <div className="au-title-block">
            <h2 className="au-title">Derslerim</h2>
            <p className="au-subtitle">Bu dönem kayıtlı olduğunuz dersler.</p>
          </div>
          <div className="au-count-pill">
            <span className="au-count-pill-dot" />
            {courses.length} ders
          </div>
        </div>

        {courses.length > 0 && (
          <div className="au-search-wrapper">
            <span className="au-search-icon">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="8" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
            </span>
            <input
              type="text"
              className="au-search-input"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Ders kodu veya ders adı ile ara..."
            />
            {searchQuery && (
              <button
                type="button"
                className="au-search-clear"
                onClick={() => setSearchQuery('')}
                aria-label="Aramayı temizle"
              >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            )}
          </div>
        )}
      </div>

      {courses.length === 0 ? (
        <div className="au-card">
          <div className="au-empty">
            <div className="au-empty-icon">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
                <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
              </svg>
            </div>
            <div className="au-empty-text">Henüz ders listeniz bulunmamaktadır</div>
          </div>
        </div>
      ) : filteredCourses.length === 0 ? (
        <div className="au-card">
          <div className="au-empty">
            <div className="au-empty-icon">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="8" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
            </div>
            <div className="au-empty-text">Arama sonucu bulunamadı</div>
            <div className="au-empty-sub">"{searchQuery}" için sonuç yok. Aramayı değiştirmeyi deneyin.</div>
          </div>
        </div>
      ) : (
        <div className="sc-grid">
          {filteredCourses.map((course) => {
            const code = course.course_code || `#${course.course_id}`;
            return (
              <div key={course.course_id} className="sc-card">
                <div className="sc-card-top">
                  <span className="sc-card-code">{code}</span>
                </div>
                <h3 className="sc-card-title">{course.course_name || 'İsimsiz Ders'}</h3>
                {(course.program_name || course.faculty_name) && (
                  <div className="sc-card-meta">
                    {course.program_name && (
                      <span className="sc-card-meta-row" title={course.program_name}>
                        {course.program_name}
                      </span>
                    )}
                    {course.faculty_name && (
                      <span className="sc-card-meta-row" title={course.faculty_name}>
                        {course.faculty_name}
                      </span>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default StudentCourses;
